import type { ChipProps } from '@mui/material';
import { AdministrationRole, GuaranteesApplicationStatus } from './taskModels.ts';
import type { TTaskRoleKey, TTaskServiceKey, TTaskStatusKey, TTaskUrgencyKey } from './types.ts';

type TTaskChipConfig = {
  label: string;
  color: ChipProps['color'];
  description: string;
};

type TTasksServiceConfig = {
  title: string;
  referenceLabel: string;
  subjectLabel: string;
  emptyText: string;
  actionPath: string;
  roles: readonly TTaskRoleKey[];
};

export const taskStatusConfig: Record<TTaskStatusKey, TTaskChipConfig> = {
  new: {
    label: 'Нова',
    color: 'default',
    description: 'Завдання створено і очікує початку роботи.',
  },
  inProgress: {
    label: 'В роботі',
    color: 'warning',
    description: 'Завдання вже в опрацюванні.',
  },
  waiting: {
    label: 'Очікує відповіді',
    color: 'default',
    description: 'Для продовження потрібна дія або відповідь.',
  },
};

export const taskGuaranteesApplicationStatusConfig: Record<
  GuaranteesApplicationStatus,
  TTaskChipConfig
> = {
  [GuaranteesApplicationStatus.DRAFT]: {
    label: 'Чернетка',
    color: 'default',
    description: 'Заявка ще не відправлена до банку.',
  },
  [GuaranteesApplicationStatus.AWAITING_CLIENT_INITIAL_SIGN]: {
    label: 'Очікує підпису клієнта',
    color: 'warning',
    description: 'Заявка очікує первинного підпису клієнтом.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_CLIENT_INITIAL_SIGN]: {
    label: 'Первинний підпис клієнта в роботі',
    color: 'info',
    description: 'Заявка перебуває на етапі первинного підпису клієнтом.',
  },
  [GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER]: {
    label: 'Очікує листа-погодження AM',
    color: 'warning',
    description: 'Очікується підготовка або погодження листа AM.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER]: {
    label: 'Лист-погодження AM в роботі',
    color: 'info',
    description: 'Лист-погодження AM опрацьовується менеджером.',
  },
  [GuaranteesApplicationStatus.AWAITING_PM_MANAGER_TERMS]: {
    label: 'Очікує умов від PM-менеджера',
    color: 'warning',
    description: 'Очікується заповнення умов PM-менеджером.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_PM_MANAGER_TERMS]: {
    label: 'Умови PM-менеджера в роботі',
    color: 'info',
    description: 'PM-менеджер опрацьовує умови заявки.',
  },
  [GuaranteesApplicationStatus.AWAITING_UDBTF_ISSUANCE_TERMS]: {
    label: 'Очікує умов випуску від УДБТФ',
    color: 'warning',
    description: 'Очікується заповнення умов випуску гарантії УДБТФ.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_ISSUANCE_TERMS]: {
    label: 'Умови випуску від УДБТФ в роботі',
    color: 'info',
    description: 'Умови випуску гарантії опрацьовуються УДБТФ.',
  },
  [GuaranteesApplicationStatus.AWAITING_INTERNAL_APPROVALS]: {
    label: 'Очікує внутрішніх погоджень',
    color: 'warning',
    description: 'Заявка очікує внутрішніх погоджень банку.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_INTERNAL_APPROVALS]: {
    label: 'Внутрішні погодження в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі внутрішніх погоджень банку.',
  },
  [GuaranteesApplicationStatus.AWAITING_CLIENT_APPROVAL_LETTER_SIGN]: {
    label: 'Очікує підпису листа-погодження клієнтом',
    color: 'warning',
    description: 'Очікується підпис листа-погодження клієнтом.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_CLIENT_APPROVAL_LETTER_SIGN]: {
    label: 'Підпис листа-погодження клієнтом в роботі',
    color: 'info',
    description: 'Лист-погодження перебуває на етапі підпису клієнтом.',
  },
  [GuaranteesApplicationStatus.AWAITING_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN]: {
    label: 'Очікує ручного підпису листа-погодження AM',
    color: 'warning',
    description: 'Очікується завантаження підписаного вручну листа-погодження AM.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_AM_APPROVAL_LETTER_SENT_MANUAL_SIGN]: {
    label: 'Ручний підпис листа-погодження AM в роботі',
    color: 'info',
    description: 'AM-менеджер опрацьовує завантаження підписаного вручну листа-погодження.',
  },
  [GuaranteesApplicationStatus.AWAITING_UAKDO_APPROVAL]: {
    label: 'Очікує погодження УАКДО',
    color: 'warning',
    description: 'Заявка очікує погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_APPROVAL]: {
    label: 'Погодження УАКДО в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.AWAITING_UAKDO_CONTROLLER_APPROVAL]: {
    label: 'Очікує контролю УАКДО',
    color: 'warning',
    description: 'Заявка очікує контрольного погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UAKDO_CONTROLLER_APPROVAL]: {
    label: 'Контроль УАКДО в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі контрольного погодження УАКДО.',
  },
  [GuaranteesApplicationStatus.AWAITING_UDBTF_APPROVAL]: {
    label: 'Очікує погодження УДБТФ',
    color: 'warning',
    description: 'Заявка очікує погодження УДБТФ.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_UDBTF_APPROVAL]: {
    label: 'Погодження УДБТФ в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі погодження УДБТФ.',
  },
  [GuaranteesApplicationStatus.AWAITING_GUARANTEE_ISSUANCE]: {
    label: 'Очікує випуску гарантії',
    color: 'warning',
    description: 'Заявка очікує випуску гарантії.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_GUARANTEE_ISSUANCE]: {
    label: 'Випуск гарантії в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі випуску гарантії.',
  },
  [GuaranteesApplicationStatus.AWAITING_USAPOK_APPROVAL]: {
    label: 'Очікує погодження УСАПОК',
    color: 'warning',
    description: 'Заявка очікує погодження УСАПОК.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_USAPOK_APPROVAL]: {
    label: 'Погодження УСАПОК в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі погодження УСАПОК.',
  },
  [GuaranteesApplicationStatus.AWAITING_USAPOK_CONTROLLER_APPROVAL]: {
    label: 'Очікує контролю УСАПОК',
    color: 'warning',
    description: 'Заявка очікує контрольного погодження УСАПОК.',
  },
  [GuaranteesApplicationStatus.IN_PROGRESS_USAPOK_CONTROLLER_APPROVAL]: {
    label: 'Контроль УСАПОК в роботі',
    color: 'info',
    description: 'Заявка опрацьовується на етапі контрольного погодження УСАПОК.',
  },
  [GuaranteesApplicationStatus.ISSUED]: {
    label: 'Гарантію випущено',
    color: 'success',
    description: 'Гарантію випущено.',
  },
  [GuaranteesApplicationStatus.COMPLETED]: {
    label: 'Завершено',
    color: 'success',
    description: 'Заявку завершено.',
  },
  [GuaranteesApplicationStatus.REJECTED]: {
    label: 'Відхилено',
    color: 'error',
    description: 'Заявку відхилено.',
  },
};

export const getTaskGuaranteesApplicationStatusConfig = (
  status?: string | null
): TTaskChipConfig | undefined => {
  const normalizedStatus = String(status ?? '').trim() as GuaranteesApplicationStatus;

  return Object.values(GuaranteesApplicationStatus).includes(normalizedStatus)
    ? taskGuaranteesApplicationStatusConfig[normalizedStatus]
    : undefined;
};

export const taskServicesConfig = {
  guarantees: {
    title: 'Гарантії',
    referenceLabel: 'Тендер / заявка',
    subjectLabel: 'Контрагент',
    emptyText: 'Наразі немає активних задач по гарантіях.',
    actionPath: '/guarantees/get-started',
    roles: [
      AdministrationRole.UAKDO_ADMINISTRATOR,
      AdministrationRole.UAKDO_CONTROLLER,
      AdministrationRole.USAPOK_ADMINISTRATOR,
      AdministrationRole.USAPOK_CONTROLLER,
      AdministrationRole.UBTF_ADMINISTRATOR,
      AdministrationRole.UBTF_CONTROLLER,
      AdministrationRole.MANAGER,
      AdministrationRole.MANAGER_CONTROLLER,
      AdministrationRole.PM_MANAGER,
      AdministrationRole.AM_MANAGER,
      AdministrationRole.UO_ADMINISTRATOR,
      AdministrationRole.UO_CONTROLLER,
      AdministrationRole.UMVN_ADMINISTRATOR,
      AdministrationRole.UMVN_CONTROLLER,
      AdministrationRole.BUSINESS_MANAGER,
      AdministrationRole.COVENANTS_BRANCH_MANAGER,
      AdministrationRole.CALL_CENTER_OPERATOR,
      AdministrationRole.PERSONAL_DATA_MANAGER,
      AdministrationRole.OBSERVER,
    ],
  },
} as const satisfies Record<TTaskServiceKey, TTasksServiceConfig>;

export const taskUrgencyConfig: Record<TTaskUrgencyKey, { label: string; description: string }> = {
  now: {
    label: 'Потрібно зараз',
    description: 'Термін виконання настав або вже минув.',
  },
  soon: {
    label: 'Скоро',
    description: 'До терміну залишилося до двох днів.',
  },
  later: {
    label: 'Планово',
    description: 'Завдання можна виконати пізніше.',
  },
};

export const taskServiceOrder: readonly TTaskServiceKey[] = ['guarantees'];

export const taskRoleLabels: Record<TTaskRoleKey, string> = {
  [AdministrationRole.UAKDO_ADMINISTRATOR]: 'Відповідальний УАКДО',
  [AdministrationRole.UAKDO_CONTROLLER]: 'Контролер УАКДО',
  [AdministrationRole.USAPOK_ADMINISTRATOR]: 'Відповідальний УСАПОК',
  [AdministrationRole.USAPOK_CONTROLLER]: 'Контролер УСАПОК',
  [AdministrationRole.UBTF_ADMINISTRATOR]: 'Відповідальний УДБТФ',
  [AdministrationRole.UBTF_CONTROLLER]: 'Контролер УДБТФ',
  [AdministrationRole.MANAGER]: 'Менеджер',
  [AdministrationRole.MANAGER_CONTROLLER]: 'Контролер менеджера',
  [AdministrationRole.PM_MANAGER]: 'PM-менеджер',
  [AdministrationRole.AM_MANAGER]: 'AM-менеджер',
  [AdministrationRole.ASSET_MANAGER]: 'AM-менеджер',
  [AdministrationRole.UO_ADMINISTRATOR]: 'Відповідальний ЮО',
  [AdministrationRole.UO_CONTROLLER]: 'Контролер ЮО',
  [AdministrationRole.UMVN_ADMINISTRATOR]: 'Відповідальний УМВН',
  [AdministrationRole.UMVN_CONTROLLER]: 'Контролер УМВН',
  [AdministrationRole.BUSINESS_MANAGER]: 'Бізнес-менеджер',
  [AdministrationRole.COVENANTS_BRANCH_MANAGER]: 'Менеджер ковенантів',
  [AdministrationRole.CALL_CENTER_OPERATOR]: 'Оператор контакт-центру',
  [AdministrationRole.PERSONAL_DATA_MANAGER]: 'Менеджер персональних даних',
  [AdministrationRole.OBSERVER]: 'Спостерігач',
};
